import { buildCommand, commandOrderId, createMemoryOutbox, isOnlineOnly } from '@didto/domain';
import type {
  CommandKind,
  CommandPayloads,
  FailedEntry,
  OutboxEntry,
  OutboxStatus,
} from '@didto/domain';
import { useSyncExternalStore } from 'react';
import { sendCommand } from './api';
import type { CommandResult } from './api';
import { PAUSED_REASON, currentConnectivity, onConnectivityChange } from './connectivity';
import { useDeviceStore } from './deviceStore';
import { isNetworkError, toAppError } from './errors';

/**
 * The POS write path (Phase 0 section J.2, ADR-012). Commands go through an in-memory outbox:
 * delivered at once while online, held in order while offline and flushed on reconnect. The
 * server's idempotent command ids make the retry safe. Persistence across reloads is Phase 13.
 *
 * ADR-024 (Phase 7A): a queued command belongs to the employee whose PIN session queued it and is
 * sent only while that employee is signed in here; the others keep flowing. A queued payment the
 * server refuses is kept as "payment not recorded" until it is sent again or discarded. Shift and
 * cash commands, and everything that gives money back, need the server now: they are never queued.
 */
const currentEmployee = () => useDeviceStore.getState().sessionEmployee?.id ?? null;
const online = () => typeof navigator === 'undefined' || navigator.onLine;
/** The outbox sends only while the server can be reached: nothing is tried against a lost connection. */
const reachable = () => online() && currentConnectivity() === 'online';

export const outbox = createMemoryOutbox<CommandResult>(
  {
    send: (envelope, locationId) => sendCommand(envelope, locationId),
    isNetworkError,
    isSessionError: (e) => {
      const code = toAppError(e).code;
      return code === 'session_required' || code === 'session_expired';
    },
  },
  {
    isOnline: reachable,
    currentEmployee,
    keepOnRefusal: (entry) => entry.envelope.kind === 'payment.capture',
  },
);

/**
 * ADR-037: a command that was on its way when the connection dropped is sent again on its own, with the
 * same id: as soon as the server can be reached again (back from offline or a lost connection), and a
 * few seconds after a dropped request while the server still answers.
 */
let retryTimer: ReturnType<typeof setTimeout> | null = null;
const RETRY_AFTER_MS = 3_000;
function retrySoon() {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (reachable()) void outbox.flush();
  }, RETRY_AFTER_MS);
}

if (typeof window !== 'undefined') {
  onConnectivityChange((next) => {
    if (next === 'online') void outbox.flush();
  });
  outbox.subscribe((s) => {
    if (!s.flushing && s.lastError === 'network' && s.pending > s.parked + s.held) retrySoon();
  });
  // the queue lives in memory until Phase 13: leaving or reloading would lose what is waiting
  window.addEventListener('beforeunload', (e) => {
    const s = outbox.status();
    if (s.pending + s.failed > 0) e.preventDefault();
  });
  // someone signed in: whatever was parked for them goes now
  useDeviceStore.subscribe((s, prev) => {
    if (s.sessionEmployee?.id && s.sessionEmployee.id !== prev.sessionEmployee?.id)
      void outbox.flush();
  });
}

let snapshot: OutboxStatus = outbox.status();
let failedSnapshot: readonly FailedEntry[] = outbox.failed();
outbox.subscribe((s) => {
  snapshot = s;
  failedSnapshot = outbox.failed();
});
const subscribe = (onChange: () => void) => outbox.subscribe(() => onChange());
const getSnapshot = () => snapshot;
const getFailed = () => failedSnapshot;

export function useOutboxStatus(): OutboxStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Queued payments the server refused, kept for the cashier to send again or discard. */
export function useFailedEntries(): readonly FailedEntry[] {
  return useSyncExternalStore(subscribe, getFailed, getFailed);
}

/** Who queued what on this tablet, so a notice can name the cashier a payment waits for. */
const employeeNames = new Map<string, string>();
export function employeeNameOf(employeeId: string | null | undefined): string | null {
  return employeeId ? (employeeNames.get(employeeId) ?? null) : null;
}

/** The money a queued command carries (a payment's tenders), for notices and the count's checks. */
export function entryAmountMinor(entry: OutboxEntry): number {
  if (entry.envelope.kind !== 'payment.capture') return 0;
  const p = entry.envelope.payload as { tenders?: { amount_minor?: number }[] };
  return (p.tenders ?? []).reduce((a, t) => a + (Number(t.amount_minor) || 0), 0);
}

/**
 * What this tablet holds that the server has not recorded: commands parked for someone not signed
 * in here and queued payments the server refused. A count of the drawer asks why it goes ahead.
 */
export function heldOnThisTablet(): { entries: number; minor: number } {
  const all = [...outbox.blocked(), ...outbox.failed().map((f) => f.entry)];
  return { entries: all.length, minor: all.reduce((a, e) => a + entryAmountMinor(e), 0) };
}

/** The error a command gets while the server cannot be reached: nothing new is queued (ADR-037). */
export class OfflineError extends Error {
  constructor() {
    super(`network: ${PAUSED_REASON}`);
  }
}

/** Run a command through the outbox. Resolves when the server applied it, however long that takes.
 * While the server cannot be reached nothing is started (ADR-037: the app is read-only offline); a
 * command already on its way when the connection drops is held and sent again with the same id. */
export async function runQueuedCommand<K extends CommandKind>(
  kind: K,
  payload: CommandPayloads[K],
  options: { commandId?: string; locationId?: string } = {},
): Promise<CommandResult> {
  const envelope = buildCommand(
    kind,
    payload,
    options.commandId ? { commandId: options.commandId } : {},
  );
  if (currentConnectivity() !== 'online' || !online()) throw new OfflineError();
  if (isOnlineOnly(kind)) return sendCommand(envelope, options.locationId);
  const who = useDeviceStore.getState().sessionEmployee;
  if (who) employeeNames.set(who.id, who.display_name);
  // ADR-024: another cashier's command on this order is waiting for them to sign in here. This one
  // would wait behind it (the order's commands stay in order), or overtake what it changes:
  // refused now, with the reason, instead of hanging or paying twice.
  const orderId = commandOrderId(envelope);
  const waiting = orderId
    ? outbox
        .blocked()
        .find(
          (e) =>
            commandOrderId(e.envelope) === orderId && !!e.employeeId && e.employeeId !== who?.id,
        )
    : undefined;
  if (waiting)
    throw new Error(
      `conflict: ${employeeNameOf(waiting.employeeId) ?? 'Another cashier'} has something on this order that is not sent yet (a payment taken offline, for example). They sign in on this tablet to send it first.`,
    );
  return outbox.enqueue(envelope, {
    ...(options.locationId ? { locationId: options.locationId } : {}),
    employeeId: who?.id ?? null,
  });
}
